import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import LoginForm from './Login/LoginForm';
import SocialLoginForm from './SocialLogin/SocialLoginForm';
import DeleteAccount from './DeleteAccount/DeleteAccount';
import AccountConversionRows from './AccountConversionRows';
import NewsletterSettingRow from './NewsletterSettingRow';
import EmailNotificationSettingRow from './EmailNotificationSettingRow';
import { Modal, Toast, useAuth, useT } from '@repo/common';
import {
  disconnectSocial,
  getAccountChangeNotices,
  dismissAccountChangeNotice,
} from '../../../api/user/user.api';

const PROVIDER_LABEL = { GOOGLE: 'Google', APPLE: 'Apple' };

const AccountSetting = () => {
  const t = useT();
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isBusiness = ['E', 'ROLE_E', 'EMPLOYER', 'ROLE_EMPLOYER'].includes(user?.role);
  const isCreator = Boolean(user?.creator ?? user?.isCreator);
  const [notices, setNotices] = useState([]);

  useEffect(() => {
    let cancelled = false;
    setNotices([]);
    if (user?.id) {
      getAccountChangeNotices()
        .then((data) => {
          if (!cancelled) setNotices(Array.isArray(data) ? data : []);
        })
        .catch((error) => console.error('Account change notices could not be loaded', error));
    }
    return () => { cancelled = true; };
  }, [user?.id, isBusiness, isCreator]);

  const dismissNotice = async (id) => {
    await dismissAccountChangeNotice(id);
    setNotices((current) => current.filter((notice) => notice.id !== id));
  };
  const startConversion = (type) => navigate('/register/additional-info', {
    state: {
      entry: 'settings',
      role: type === 'business' ? 'E' : 'U',
      isCreator: type === 'creator',
      returnTo: '/mypage?tab=Account%20setting',
    },
  });
  const [openForm, setOpenForm] = useState(null);
  const [showDeleteSuccessToast, setShowDeleteSuccessToast] = useState(false);
  const [showUpdateEmailSuccessToast, setShowUpdateEmailSuccessToast] =
    useState(false);
  const [showUpdatePasswordSuccessToast, setShowUpdatePasswordSuccessToast] =
    useState(false);
  const [connectFailed, setConnectFailed] = useState(false);
  // { provider: 'GOOGLE' | 'APPLE', reason: string }
  const [disconnectModal, setDisconnectModal] = useState(null);

  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const isDisconnectOpen = disconnectModal !== null;
  const provider = disconnectModal?.provider ?? 'GOOGLE';
  const reason = disconnectModal?.reason ?? null;
  const providerLabel = PROVIDER_LABEL[provider] ?? provider;

  /*
   * 애플 연동은 애플 페이지를 거쳐 돌아오므로 결과가 쿼리로 들어온다.
   * 한 번 읽고 주소에서 지운다 — 새로고침 때 토스트가 다시 뜨지 않도록.
   */
  useEffect(() => {
    const result = searchParams.get('appleConnect');
    if (!result) return;

    if (result === 'success') {
      refreshUser();
      setShowDeleteSuccessToast(false);
    } else {
      setConnectFailed(true);
    }

    searchParams.delete('appleConnect');
    searchParams.delete('reason');
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams, refreshUser]);

  return (
    <div className='relative flex flex-col max-w-[568px] w-full items-start tabletH:pb-[60px]'>
      <div
        className={
          isDisconnectOpen || isDeleteOpen
            ? 'pointer-events-none [&_*]:text-text-disable [&_svg]:opacity-30 w-full'
            : 'w-full'
        }
      >
        <LoginForm
          openForm={openForm}
          onOpenFormChange={setOpenForm}
          onEmailSuccess={() => {
            setShowUpdateEmailSuccessToast(false);
            setTimeout(() => setShowUpdateEmailSuccessToast(true), 0);
          }}
          onPasswordSuccess={() => {
            setShowUpdatePasswordSuccessToast(false);
            setTimeout(() => setShowUpdatePasswordSuccessToast(true), 0);
          }}
        />
      </div>

      <div
        className={`w-full ${
          openForm || isDeleteOpen
            ? 'pointer-events-none [&_*]:text-text-disable [&_svg]:opacity-30'
            : ''
        }`}
      >
        <SocialLoginForm onDisconnectOpen={setDisconnectModal} />
      </div>

      <div
        className={`w-full ${
          openForm || isDisconnectOpen
            ? 'pointer-events-none [&_*]:text-text-disable'
            : ''
        }`}
      >
        <DeleteAccount onDeleteOpen={setIsDeleteOpen}>
          <AccountConversionRows isBusiness={isBusiness} isCreator={isCreator} onConvert={startConversion} notices={notices} onDismiss={dismissNotice} />
          <EmailNotificationSettingRow />
          <NewsletterSettingRow />
        </DeleteAccount>
      </div>

      {/* 이메일 없음 + 비밀번호 없음 */}
      {reason === 'no-email-no-pw' && (
        <Modal
          isOpen
          title={t(
            `Add an email and password\nto disconnect ${providerLabel}`,
            `${providerLabel} 연결을 해제하려면\n이메일과 비밀번호를 추가해 주세요`,
          )}
          leftButtonLabel={t('cancel', '취소')}
          rightButtonLabel={t('Set up login', '로그인 설정')}
          leftHideIcon
          rightHideIcon
          type='notice'
          onClickLeftBtn={() => setDisconnectModal(null)}
          onClickRightBtn={() => {
            setDisconnectModal(null);
            setOpenForm('email');
          }}
        />
      )}

      {/* 이메일 있음 + 비밀번호 없음 */}
      {reason === 'email-no-pw' && (
        <Modal
          isOpen
          title={t(
            `Set a password before disconnecting ${providerLabel}`,
            `${providerLabel} 연결 해제 전에 비밀번호를 설정해 주세요`,
          )}
          leftButtonLabel={t('cancel', '취소')}
          rightButtonLabel={t('Set password', '비밀번호 설정')}
          leftHideIcon
          rightHideIcon
          type='notice'
          onClickLeftBtn={() => setDisconnectModal(null)}
          onClickRightBtn={() => {
            setDisconnectModal(null);
            setOpenForm('password');
          }}
        />
      )}

      {/* 이메일 없음 + 비밀번호 있음 */}
      {reason === 'no-email-has-pw' && (
        <Modal
          isOpen
          title={t(
            `Add an email before disconnecting ${providerLabel}`,
            `${providerLabel} 연결 해제 전에 이메일을 추가해 주세요`,
          )}
          leftButtonLabel={t('cancel', '취소')}
          rightButtonLabel={t('Add email', '이메일 추가')}
          leftHideIcon
          rightHideIcon
          type='notice'
          onClickLeftBtn={() => setDisconnectModal(null)}
          onClickRightBtn={() => {
            setDisconnectModal(null);
            setOpenForm('email');
          }}
        />
      )}

      {/* 다시 로그인할 수단이 남아 있는 경우 */}
      {reason === 'can-disconnect' && (
        <Modal
          isOpen
          title={t(
            `Disconnect ${providerLabel} account?`,
            `${providerLabel} 계정 연결을 해제할까요?`,
          )}
          leftButtonLabel={t('Cancel', '취소')}
          rightButtonLabel={t('Disconnect', '연결 해제')}
          leftHideIcon
          rightHideIcon
          type='warning'
          onClickLeftBtn={() => setDisconnectModal(null)}
          onClickRightBtn={async () => {
            await disconnectSocial(provider);
            await refreshUser();
            setDisconnectModal(null);
            setShowDeleteSuccessToast(providerLabel);
          }}
        />
      )}

      {showDeleteSuccessToast && (
        <div className='fixed bottom-6 left-0 right-0 px-4 z-[1000] flex justify-center'>
          <Toast
            type='success'
            state='white'
            title={t(
              `${showDeleteSuccessToast} account disconnected.`,
              `${showDeleteSuccessToast} 계정 연결이 해제되었습니다.`,
            )}
            direction='up'
            customWidth='auto'
          />
        </div>
      )}

      {connectFailed && (
        <div className='fixed bottom-6 left-0 right-0 px-4 z-[1000] flex justify-center'>
          <Toast
            type='error'
            state='white'
            title={t(
              "Couldn't connect that account. It may already be linked to another account.",
              '연결하지 못했습니다. 이미 다른 계정에 연결된 소셜 계정일 수 있습니다.',
            )}
            direction='up'
            customWidth='auto'
          />
        </div>
      )}

      {showUpdateEmailSuccessToast && (
        <div className='fixed bottom-6 left-0 right-0 px-4 z-[1000] flex justify-center'>
          <Toast
            type='success'
            state='white'
            title={t('Email updated.', '이메일이 변경되었습니다.')}
            direction='up'
            customWidth='auto'
          />
        </div>
      )}

      {showUpdatePasswordSuccessToast && (
        <div className='fixed bottom-6 left-0 right-0 px-4 z-[1000] flex justify-center'>
          <Toast
            type='success'
            state='white'
            title={t('Password created.', '비밀번호가 생성되었습니다.')}
            direction='up'
            customWidth='auto'
          />
        </div>
      )}
    </div>
  );
};

export default AccountSetting;
